import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Eye, EyeSlash, CheckCircle, Lock, User as UserIcon, Binoculars } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

import API_URL from '../config';

export default function SettingsPage() {
  const { user, isAdmin } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cadence, setCadence] = useState(null);
  const [cadenceSaving, setCadenceSaving] = useState(false);

  const Layout = isAdmin ? AdminLayout : DashboardLayout;

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API_URL}/api/admin/brand-watch-settings`)
      .then((r) => setCadence(r.data))
      .catch(() => {});
  }, [isAdmin]);

  const saveCadence = async (value) => {
    if (cadence?.cadence === value) return;
    setCadenceSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/admin/brand-watch-settings`, { cadence: value });
      setCadence(r.data);
      toast.success(`Brand Watch cadence set to ${value}`);
    } catch (e) {
      toast.error("Couldn't update cadence");
    } finally {
      setCadenceSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to change password';
      toast.error(typeof msg === 'string' ? msg : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto" data-testid="settings-page">
        <div className="mb-8">
          <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Account</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d' }}>Settings</h1>
        </div>

        {/* Profile Info */}
        <div className="card-swiss mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 flex items-center justify-center" style={{ backgroundColor: '#f2e9db' }}>
              <UserIcon weight="duotone" className="w-7 h-7" style={{ color: '#141b4d' }} />
            </div>
            <div>
              <p className="text-lg font-semibold" style={{ color: '#141b4d' }}>{user?.name}</p>
              <p className="text-sm" style={{ color: '#9a9488' }}>{user?.email}</p>
              {user?.company && <p className="text-xs" style={{ color: '#9a9488' }}>{user.company}</p>}
            </div>
          </div>
        </div>

        {/* Brand Watch cadence (admin only) */}
        {isAdmin && (
          <div className="card-swiss mb-6" data-testid="brand-watch-cadence-card">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b" style={{ borderColor: '#d4cfc5' }}>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#141b4d' }}>
                <Binoculars weight="bold" className="w-5 h-5" style={{ color: '#ffd100' }} />
              </div>
              <div>
                <p className="font-medium" style={{ color: '#141b4d' }}>Brand Watch cadence</p>
                <p className="text-sm" style={{ color: '#9a9488' }}>How often we review new look-alike filings. Controls the wording clients see.</p>
              </div>
            </div>
            <div className="flex gap-3">
              {['quarterly', 'monthly'].map((opt) => {
                const active = cadence?.cadence === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => saveCadence(opt)}
                    disabled={cadenceSaving}
                    data-testid={`cadence-option-${opt}`}
                    className="flex-1 px-4 py-3 border text-sm font-semibold capitalize transition-colors disabled:opacity-60"
                    style={active
                      ? { backgroundColor: '#141b4d', color: '#fff', borderColor: '#141b4d' }
                      : { backgroundColor: '#fff', color: '#3f3f46', borderColor: '#d4cfc5' }}
                  >
                    {opt}{opt === 'quarterly' ? ' (default)' : ''}
                  </button>
                );
              })}
            </div>
            {cadence?.next_update_label && (
              <p className="text-xs mt-3" style={{ color: '#9a9488' }} data-testid="cadence-next-update">
                Clients currently see: reviews {cadence.every}; next update {cadence.next_update_label}.
              </p>
            )}
          </div>
        )}

        {/* Change Password */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-swiss"
        >
          <div className="flex items-center gap-3 mb-6 pb-4 border-b" style={{ borderColor: '#d4cfc5' }}>
            <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#ffd100' }}>
              <Lock weight="bold" className="w-5 h-5" style={{ color: '#141b4d' }} />
            </div>
            <div>
              <p className="font-medium" style={{ color: '#141b4d' }}>Change Password</p>
              <p className="text-sm" style={{ color: '#9a9488' }}>Update your account password</p>
            </div>
          </div>

          {success && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-[#22C55E]/10 text-[#22C55E] text-sm" data-testid="password-changed-success">
              <CheckCircle weight="fill" className="w-5 h-5" />
              Password changed successfully
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="label-uppercase block mb-2">Current Password</label>
              <div className="relative">
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-swiss pr-12"
                  placeholder="Enter your current password"
                  required
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#9a9488' }}>
                  {showPasswords ? <EyeSlash weight="regular" className="w-5 h-5" /> : <Eye weight="regular" className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="label-uppercase block mb-2">New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-swiss"
                placeholder="At least 6 characters"
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>

            <div>
              <label className="label-uppercase block mb-2">Confirm New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-swiss"
                placeholder="Re-enter new password"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="change-password-btn"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-[#141b4d]/20 border-t-[#141b4d] rounded-full animate-spin"></div>Changing...</>
              ) : 'Change Password'}
            </button>
          </form>
        </motion.div>
      </div>
    </Layout>
  );
}
